import React, { useEffect, useState } from "react";
import { Loader2, X } from "lucide-react";
import { toast } from "react-toastify";
import { api } from "../../lib/axios";

function apiErrorMessage(err: unknown, fallback: string): string {
  const ax = err as {
    response?: { data?: { error?: string | { message?: string } } };
  };
  const d = ax.response?.data?.error;
  if (typeof d === "string") return d;
  if (d && typeof d === "object" && "message" in d && typeof (d as { message?: string }).message === "string") {
    return (d as { message: string }).message;
  }
  return fallback;
}

type Province = { id: number; nom: string };
type Ville = { id: number; nom: string; provinceId?: number };
type UserOpt = { id: number; nom: string; prenom?: string | null };

export type ModalZoneOrganisationCreateProps = {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
};

const inputClass =
  "mt-1.5 w-full rounded-xl border border-slate-700 bg-slate-950/60 px-3 py-2 text-sm text-white outline-none focus:ring-2 focus:ring-emerald-500/30";

export default function ModalZoneOrganisationCreate({
  open,
  onClose,
  onCreated,
}: ModalZoneOrganisationCreateProps): React.JSX.Element | null {
  const [nom, setNom] = useState("");
  const [provinceId, setProvinceId] = useState<number | "">("");
  const [villeId, setVilleId] = useState<number | "">("");
  const [communeLibre, setCommuneLibre] = useState("");
  const [responsableId, setResponsableId] = useState<number | "">("");
  const [populationEstimee, setPopulationEstimee] = useState<number | "">("");
  const [commentaire, setCommentaire] = useState("");
  const [actif, setActif] = useState(true);
  const [loading, setLoading] = useState(false);
  const [metaLoading, setMetaLoading] = useState(false);
  const [provinces, setProvinces] = useState<Province[]>([]);
  const [villes, setVilles] = useState<Ville[]>([]);
  const [users, setUsers] = useState<UserOpt[]>([]);

  useEffect(() => {
    if (!open) {
      setNom("");
      setProvinceId("");
      setVilleId("");
      setCommuneLibre("");
      setResponsableId("");
      setPopulationEstimee("");
      setCommentaire("");
      setActif(true);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setMetaLoading(true);
    void (async () => {
      try {
        const [pRes, uRes] = await Promise.all([
          api.get("/geographie/provinces").catch(() => ({ data: [] })),
          api.get("/users").catch(() => ({ data: [] })),
        ]);
        const pRaw = Array.isArray(pRes.data) ? pRes.data : [];
        setProvinces(pRaw);
        const uRaw = Array.isArray(uRes.data) ? uRes.data : [];
        setUsers(
          uRaw.map((u: UserOpt) => ({
            id: u.id,
            nom: u.nom,
            prenom: u.prenom,
          }))
        );
      } catch {
        setProvinces([]);
        setUsers([]);
      } finally {
        setMetaLoading(false);
      }
    })();
  }, [open]);

  useEffect(() => {
    if (!open || provinceId === "") {
      setVilles([]);
      setVilleId("");
      return;
    }
    void (async () => {
      try {
        const res = await api.get("/geographie/villes", { params: { provinceId: Number(provinceId) } });
        const raw = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
        setVilles(raw);
      } catch {
        setVilles([]);
        toast.error("Impossible de charger les villes.", { toastId: "org-terrain-villes" });
      }
    })();
  }, [open, provinceId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nom.trim()) {
      toast.error("Le nom de la zone est obligatoire.");
      return;
    }

    const descParts: string[] = [];
    if (communeLibre.trim()) descParts.push(`Commune : ${communeLibre.trim()}`);
    if (commentaire.trim()) descParts.push(commentaire.trim());
    const description = descParts.length > 0 ? descParts.join("\n\n") : null;

    setLoading(true);
    try {
      await api.post("/zones", {
        nom: nom.trim(),
        actif,
        provinceId: provinceId === "" ? null : Number(provinceId),
        villeId: villeId === "" ? null : Number(villeId),
        description,
        responsableId: responsableId === "" ? null : Number(responsableId),
        populationEstimee:
          populationEstimee === "" ? null : Math.max(0, Math.floor(Number(populationEstimee))),
      });
      toast.success("Zone créée.", { toastId: "org-terrain-zone-ok" });
      onCreated();
      onClose();
    } catch (err: unknown) {
      if ((err as { response?: { status?: number } }).response?.status === 409) {
        toast.error(apiErrorMessage(err, "Une zone avec ce nom existe déjà."), {
          toastId: "org-terrain-zone-409",
        });
      } else {
        toast.error(apiErrorMessage(err, "Impossible de créer la zone."), {
          toastId: "org-terrain-zone-err",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
      <div
        className="relative max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl border border-slate-700 bg-slate-900 p-6 shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-zone-org-title"
      >
        <button
          type="button"
          className="absolute right-4 top-4 rounded-lg p-1 text-slate-400 hover:bg-slate-800 hover:text-white"
          onClick={onClose}
          aria-label="Fermer"
        >
          <X className="h-5 w-5" />
        </button>

        <h2 id="modal-zone-org-title" className="text-lg font-bold text-white">
          Créer une zone
        </h2>
        <p className="mt-1 text-sm text-slate-400">
          Définissez la zone opérationnelle ; vous pourrez affiner les rattachements commune / quartier depuis la page Zones si besoin.
        </p>

        <form onSubmit={(e) => void handleSubmit(e)} className="mt-6 space-y-4">
          <label className="block text-sm font-medium text-slate-300">
            Nom de la zone *
            <input value={nom} onChange={(e) => setNom(e.target.value)} className={inputClass} required />
          </label>

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block text-sm font-medium text-slate-300">
              Province
              <select
                value={provinceId === "" ? "" : String(provinceId)}
                onChange={(e) => setProvinceId(e.target.value === "" ? "" : Number(e.target.value))}
                className={inputClass}
                disabled={metaLoading}
              >
                <option value="">—</option>
                {provinces.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nom}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm font-medium text-slate-300">
              Ville
              <select
                value={villeId === "" ? "" : String(villeId)}
                onChange={(e) => setVilleId(e.target.value === "" ? "" : Number(e.target.value))}
                className={inputClass}
                disabled={provinceId === ""}
              >
                <option value="">—</option>
                {villes.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.nom}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label className="block text-sm font-medium text-slate-300">
            Commune (libre)
            <input
              value={communeLibre}
              onChange={(e) => setCommuneLibre(e.target.value)}
              className={inputClass}
              placeholder="Texte — sera ajouté à la description"
            />
          </label>

          <label className="block text-sm font-medium text-slate-300">
            Responsable (utilisateur)
            <select
              value={responsableId === "" ? "" : String(responsableId)}
              onChange={(e) => setResponsableId(e.target.value === "" ? "" : Number(e.target.value))}
              className={inputClass}
              disabled={metaLoading}
            >
              <option value="">—</option>
              {users
                .sort((a, b) => a.nom.localeCompare(b.nom))
                .map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.prenom ?? ""} {u.nom}
                  </option>
                ))}
            </select>
          </label>

          <label className="block text-sm font-medium text-slate-300">
            Capacité / charge estimée (indicatif)
            <input
              type="number"
              min={0}
              value={populationEstimee === "" ? "" : populationEstimee}
              onChange={(e) =>
                setPopulationEstimee(e.target.value === "" ? "" : Math.max(0, parseInt(e.target.value, 10) || 0))
              }
              className={inputClass}
            />
            <span className="mt-1 block text-xs text-slate-500">
              Stocké comme population / volume estimé côté zone (champ existant).
            </span>
          </label>

          <label className="block text-sm font-medium text-slate-300">
            Commentaire
            <textarea
              value={commentaire}
              onChange={(e) => setCommentaire(e.target.value)}
              rows={2}
              className={inputClass}
            />
          </label>

          <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-300">
            <input
              type="checkbox"
              checked={actif}
              onChange={(e) => setActif(e.target.checked)}
              className="rounded border-slate-600"
            />
            Zone active
          </label>

          <div className="flex justify-end gap-2 border-t border-slate-800 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-slate-600 px-4 py-2 text-sm text-slate-300 hover:bg-slate-800"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500 disabled:opacity-50"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Créer la zone
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
